import { useRef, useCallback, useEffect } from 'react';

export interface MouseTrackingData {
  trajectory: Array<{
    x: number;
    y: number;
    t: number;
  }>;
  measures?: {
    totalDistance: number;
    directDistance: number;
    maxDeviation: number;
    areaUnderCurve: number;
    averageVelocity: number;
    maxVelocity: number;
    accelerationChanges: number;
    xFlips: number;
    yFlips: number;
    responseTime: number;
    initiationTime: number;
    movementTime: number;
    hoverDurations: Record<string, number>;
  };
}

export const useMouseTracking = <T extends HTMLElement>(targetRef: React.RefObject<T | null>) => {
  const trajectoryRef = useRef<MouseTrackingData['trajectory']>([]);
  const startTimeRef = useRef<number>(0);
  const firstMoveTimeRef = useRef<number>(0);
  const hoverStartRef = useRef<{ element: string; time: number } | null>(null);
  const hoverDurationsRef = useRef<Record<string, number>>({});

  const reset = useCallback(() => {
    trajectoryRef.current = [];
    startTimeRef.current = Date.now();
    firstMoveTimeRef.current = 0;
    hoverStartRef.current = null;
    hoverDurationsRef.current = {};
  }, []);

  const recordPoint = useCallback((e: MouseEvent) => {
    const now = Date.now();
    
    if (!firstMoveTimeRef.current && trajectoryRef.current.length === 0) {
      firstMoveTimeRef.current = now;
    }

    if (!targetRef.current) return;
    const rect = targetRef.current.getBoundingClientRect();
    if (!rect) return;

    // Normalize coordinates relative to the container
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    
    trajectoryRef.current.push({
      x,
      y,
      t: now - startTimeRef.current
    });

    // Track hover over elements
    const target = e.target as HTMLElement;
    const elementId = target.getAttribute('data-track-id') || target.className;
    
    if (elementId !== hoverStartRef.current?.element) {
      if (hoverStartRef.current) {
        const duration = now - hoverStartRef.current.time;
        hoverDurationsRef.current[hoverStartRef.current.element] = 
          (hoverDurationsRef.current[hoverStartRef.current.element] || 0) + duration;
      }
      hoverStartRef.current = { element: elementId, time: now };
    }
  }, [targetRef]);

  const calculateMeasures = useCallback((): MouseTrackingData['measures'] => {
    const trajectory = trajectoryRef.current;
    if (trajectory.length < 2) return undefined;

    const first = trajectory[0];
    const last = trajectory[trajectory.length - 1];
    
    // Basic distance measures
    const directDistance = Math.sqrt(
      Math.pow(last.x - first.x, 2) + Math.pow(last.y - first.y, 2)
    );
    
    let totalDistance = 0;
    let maxDeviation = 0;
    let areaUnderCurve = 0;
    const velocities: number[] = [];
    let xFlips = 0;
    let yFlips = 0;
    let lastXDirection = 0;
    let lastYDirection = 0;
    let accelerationChanges = 0;
    let lastAcceleration = 0;

    for (let i = 1; i < trajectory.length; i++) {
      const prev = trajectory[i - 1];
      const curr = trajectory[i];
      
      // Distance
      const segmentDistance = Math.sqrt(
        Math.pow(curr.x - prev.x, 2) + Math.pow(curr.y - prev.y, 2)
      );
      totalDistance += segmentDistance;
      
      // Velocity
      const timeDiff = curr.t - prev.t;
      if (timeDiff > 0) {
        const velocity = segmentDistance / timeDiff * 1000; // pixels per second
        velocities.push(velocity);
        
        // Acceleration
        if (i > 1 && velocities.length > 1) {
          const prevVelocity = velocities[velocities.length - 2];
          const acceleration = (velocity - prevVelocity) / timeDiff;
          
          if (lastAcceleration !== 0 && Math.sign(acceleration) !== Math.sign(lastAcceleration)) {
            accelerationChanges++;
          }
          lastAcceleration = acceleration;
        }
      }
      
      // Direction changes (flips)
      const xDirection = Math.sign(curr.x - prev.x);
      const yDirection = Math.sign(curr.y - prev.y);
      
      if (lastXDirection !== 0 && xDirection !== 0 && xDirection !== lastXDirection) {
        xFlips++;
      }
      if (lastYDirection !== 0 && yDirection !== 0 && yDirection !== lastYDirection) {
        yFlips++;
      }
      
      lastXDirection = xDirection;
      lastYDirection = yDirection;
      
      // Maximum deviation from straight line
      const lineProgress = i / (trajectory.length - 1);
      const expectedX = first.x + (last.x - first.x) * lineProgress;
      const expectedY = first.y + (last.y - first.y) * lineProgress;
      const deviation = Math.sqrt(
        Math.pow(curr.x - expectedX, 2) + Math.pow(curr.y - expectedY, 2)
      );
      maxDeviation = Math.max(maxDeviation, deviation);
      
      // Area under curve (simplified)
      areaUnderCurve += deviation * (timeDiff / 1000);
    }

    return {
      totalDistance,
      directDistance,
      maxDeviation,
      areaUnderCurve,
      averageVelocity: velocities.length > 0 
        ? velocities.reduce((a, b) => a + b, 0) / velocities.length 
        : 0,
      maxVelocity: velocities.length > 0 ? Math.max(...velocities) : 0,
      accelerationChanges,
      xFlips,
      yFlips,
      responseTime: last.t,
      initiationTime: firstMoveTimeRef.current - startTimeRef.current,
      movementTime: last.t - (firstMoveTimeRef.current - startTimeRef.current),
      hoverDurations: { ...hoverDurationsRef.current }
    };
  }, []);

  useEffect(() => {
    const element = targetRef.current;
    if (!element) return;

    element.addEventListener('mousemove', recordPoint);
    
    return () => {
      element.removeEventListener('mousemove', recordPoint);
    };
  }, [recordPoint, targetRef]);

  return {
    reset,
    getTrajectory: () => trajectoryRef.current,
    getMeasures: calculateMeasures
  };
};